import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material';
import { TeamVm } from 'api/management/team.vm';
import { GetTeamsVm } from 'api/management/get-teams.vm';
import { getTeams } from 'api/management/get-teams';
import { useSnackbar } from 'hooks/use-snackbar';

interface TeamSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (teamId: string) => void;
  loading?: boolean;
  warehouseId: string;
}

const TeamSelectionDialog: React.FC<TeamSelectionDialogProps> = ({ open, onClose, onSubmit, loading = false, warehouseId }) => {
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [teams, setTeams] = useState<TeamVm[]>([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const snackbar = useSnackbar();

  const fetchTeams = () => {
    setTeamsLoading(true);
    getTeams('', warehouseId, 'PICKING', {
      success: (response: GetTeamsVm) => {
        setTeams(response.objects);
        setTeamsLoading(false);
      },
      error: (err) => {
        console.error('Falha ao obter equipas:', err);
        snackbar.show('Falha ao carregar equipas', null, 'error');
        setTeamsLoading(false);
      },
    });
  };

  useEffect(() => {
    if (open) {
      fetchTeams();
      setSelectedTeamId('');
    }
  }, [open, warehouseId]);

  const handleSubmit = () => {
    if (selectedTeamId) {
      onSubmit(selectedTeamId);
    }
  };

  const handleClose = () => {
    setSelectedTeamId('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Selecionar Equipa</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          {teamsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress />
            </Box>
          ) : teams.length === 0 ? (
            <Typography color="text.secondary" align="center">
              Nenhuma equipa de expedição disponível neste armazém
            </Typography>
          ) : (
            <FormControl fullWidth required>
              <InputLabel>Equipa</InputLabel>
              <Select value={selectedTeamId} label="Equipa" onChange={(e) => setSelectedTeamId(e.target.value)} disabled={loading}>
                {teams.map((team) => (
                  <MenuItem key={team.id} value={team.id}>
                    {team.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!selectedTeamId || loading || teams.length === 0}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'A atribuir...' : 'Atribuir'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TeamSelectionDialog;
